import {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
  forwardRef,
  useImperativeHandle
} from 'react';
import { dialogueSample } from '../fixtures/uiKitFixtures';

/** Delay between characters; stable line breaks (every glyph in flow, no Motion). */
const MS_PER_CHAR = 15;

/**
 * @param {object} props
 * @param {string} [props.text] — если не задан, показывается фикстура UI kit
 * @param {string} [props.speakerLabel]
 * @param {string|number} [props.lineKey] — смена сбрасывает анимацию
 * @param {() => void} [props.onLineComplete] — строка полностью показана (или скип); родитель может открыть ответы
 * @param {() => void} [props.onContinue] — после полной строки: клик / Enter / пробел для продолжения (без ответов)
 */
const GameDialogueBar = forwardRef(function GameDialogueBar(
  { text: textProp, speakerLabel = 'Narration', lineKey = 'default', onLineComplete, onContinue },
  ref
) {
  const useFixture = textProp === undefined;
  const text = useMemo(
    () => (useFixture ? dialogueSample : String(textProp ?? '')),
    [useFixture, textProp]
  );
  const chars = useMemo(() => text.split(''), [text]);

  const [visible, setVisible] = useState(0);
  const timeoutRef = useRef(null);
  const completeFiredRef = useRef(false);

  const clearTimer = useCallback(() => {
    if (timeoutRef.current != null) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  const skipToEnd = useCallback(() => {
    clearTimer();
    setVisible(text.length);
  }, [clearTimer, text.length]);

  useEffect(() => {
    completeFiredRef.current = false;
    setVisible(0);
    let cancelled = false;

    const step = (count) => {
      if (cancelled) return;
      const next = Math.min(count + 1, text.length);
      setVisible(next);
      if (next < text.length) {
        timeoutRef.current = window.setTimeout(() => step(next), MS_PER_CHAR);
      }
    };

    step(0);

    return () => {
      cancelled = true;
      clearTimer();
    };
  }, [text, lineKey, clearTimer]);

  useEffect(() => {
    if (!onLineComplete || useFixture) return;
    if (visible < text.length) return;
    if (completeFiredRef.current) return;
    completeFiredRef.current = true;
    onLineComplete();
  }, [visible, text.length, onLineComplete, useFixture]);

  const typing = visible < text.length;
  const canContinue = !typing && !useFixture && typeof onContinue === 'function';

  useImperativeHandle(
    ref,
    () => ({
      advance: () => {
        if (useFixture) return;
        if (visible < text.length) skipToEnd();
        else if (canContinue) onContinue();
      }
    }),
    [useFixture, visible, text.length, canContinue, skipToEnd, onContinue]
  );

  const handleBarClick = () => {
    if (typing) skipToEnd();
    else if (canContinue) onContinue();
  };

  const interactive = typing || canContinue;

  useEffect(() => {
    if (useFixture || !interactive) return;
    const onKey = (e) => {
      if (e.key !== 'Enter' && e.key !== ' ') return;
      e.preventDefault();
      if (visible < text.length) skipToEnd();
      else if (canContinue) onContinue();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [useFixture, interactive, visible, text.length, canContinue, skipToEnd, onContinue]);

  return (
    <div className="w-full border-t border-white/10 bg-gradient-to-t from-game-canvas via-game-canvas/95 to-transparent px-4 py-4">
      <div
        className={`mx-auto max-w-3xl rounded-xl border border-white/10 bg-game-tint px-4 py-3 ${
          interactive ? 'cursor-pointer select-none' : ''
        }`}
        onClick={interactive ? handleBarClick : undefined}
        role={interactive ? 'button' : undefined}
        tabIndex={interactive ? 0 : undefined}
        aria-label={
          typing ? 'Tap to show full line' : canContinue ? 'Tap to continue' : undefined
        }
      >
        <p className="mb-2 text-[10px] font-semibold uppercase tracking-wide text-game-muted">{speakerLabel}</p>
        <p className="min-h-[4.5rem] text-left text-base leading-relaxed text-game-text/95">
          {chars.map((ch, i) => (
            <span key={i} className={i < visible ? '' : 'text-transparent'}>
              {ch}
            </span>
          ))}
        </p>
      </div>
    </div>
  );
});

export default GameDialogueBar;
